import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { Accordion } from 'components/molecules/Accordion';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { EPerPages, ISortOptions } from 'models/table';
import { useTransactions } from 'pages/TransactionsPage/hooks';
import { Pagination } from 'components/molecules/Pagination';
import { TransactionDetailsMarkingsCard } from './TransactionDetailsMarkingsCard';
import { EMarkingTypes } from 'constants/Markings';
import { StyledDetailsWrapper } from 'pages/TransactionsPage/ViewTransactionPage/styles';
import { ETransactionTypes } from 'constants/TransactionTypes';
import {
  EAdditionalFields,
  getAdditionalFields,
  getMarkingType,
  mapTransactionDetailsMarkings,
  transactionDetailsMarkingsTableColumns,
} from './helpers';
import { DetailsMarkingsItem } from './DetailsMarkingsItem';
import { EArtAncillaryTypeName } from 'constants/ArtAccessoryType';

interface ITransactionDetailsMarkingsSection {
  id: string;
  isAllExpanded: boolean;
  transactionType?: ETransactionTypes;
  ancillaryDictionaryValue?: string;
  [key: string]: any;
}
export enum ETransactionDetailsTranslationsKeys {
  noResults = 'users.search.noResults.info',
}

const TransactionDetailsMarkingsSection = ({
  id,
  isAllExpanded,
  transactionType,
  ancillaryDictionaryValue,
}: ITransactionDetailsMarkingsSection) => {
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const [isMarkingsPaginationLoading, setIsMarkingsPaginationLoading] = useState(false);
  const [currentPageMarkings, setCurrentPageMarkings] = useState<number>(1);
  const [perPageMarkings, setPerPageMarkings] = useState<EPerPages>(EPerPages.perPage25);
  const [sortMarkings, setSortMarkings] = useState<ISortOptions | null>(null);
  const [changedMarkingsList, setChangedMarkingsList] = useState<EMarkingTypes[]>([]);
  const [fieldsForTransactionType, setFieldsForTransactionType] = useState<
    (EMarkingTypes | EAdditionalFields)[]
  >([]);
  const [anyWasAssetImported, setAnyWasAssetImported] = useState(false);
  const {
    handleGetTransactionDetailsMarkingsData,
    transactionDetailsMarkingsContent,
    transactionDetailsMarkingsPaginator,
  } = useTransactions();

  useEffect(() => {
    if (transactionType) {
      let markingType = undefined;
      if (transactionType === ETransactionTypes.MARKED) {
        if (
          (!changedMarkingsList.length && anyWasAssetImported) ||
          (changedMarkingsList.includes(EMarkingTypes.IMPORT) &&
            !changedMarkingsList.includes(EMarkingTypes.APPLIED))
        ) {
          markingType = EMarkingTypes.IMPORT;
        } else if (
          (!changedMarkingsList.length && !anyWasAssetImported) ||
          (!changedMarkingsList.includes(EMarkingTypes.IMPORT) &&
            changedMarkingsList.includes(EMarkingTypes.APPLIED))
        ) {
          markingType = EMarkingTypes.APPLIED;
        }
      }
      const markingsFields = getMarkingType(transactionType, markingType);

      const isAmmunitionType = ancillaryDictionaryValue === EArtAncillaryTypeName.AMMUNITION;

      const additionalFields: EAdditionalFields[] = getAdditionalFields(
        transactionType,
        isAmmunitionType,
      );
      setFieldsForTransactionType([...markingsFields, ...additionalFields]);
    }
  }, [transactionType, changedMarkingsList, anyWasAssetImported, ancillaryDictionaryValue]);

  useEffect(() => {
    (async function getTransactionDetailsMarkingsInit() {
      if (id) {
        setIsMarkingsPaginationLoading(true);
        await handleGetTransactionDetailsMarkingsData(
          id,
          currentPageMarkings,
          perPageMarkings,
          sortMarkings,
        );
        setIsMarkingsPaginationLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sortMarkings, perPageMarkings, currentPageMarkings]);

  useEffect(() => {
    if (
      transactionType &&
      transactionType === ETransactionTypes.MARKED &&
      transactionDetailsMarkingsContent.length
    ) {
      const uniqueMarkingTypes = [
        ...new Set(
          transactionDetailsMarkingsContent
            .flatMap((item) => item.markings ?? [])
            .filter((m) => m.addedByTransaction === true)
            .map((m) => m.markingType),
        ),
      ];
      setChangedMarkingsList(uniqueMarkingTypes);
      setAnyWasAssetImported(
        transactionDetailsMarkingsContent.filter((m) => m.wasAssetImported === true).length >
        0,
      );
    }
  }, [transactionType, transactionDetailsMarkingsContent]);

  if (!fieldsForTransactionType.length) return <></>;

  if (transactionDetailsMarkingsContent && transactionDetailsMarkingsContent.length > 1) {
    return (
      <Accordion
        title={t(
          transactionType === ETransactionTypes.REPURPOSED
            ? 'transactionDetails.repurposedItemUpdates.accordion.name'
            : 'transactionDetails.markings.accordion.name',
        )}
        name="markingsDetails"
        expanded={isAllExpanded}
      >
        {transactionDetailsMarkingsContent?.length > 1 && (
          <Grid size={{ xs: 12 }}>
            {isDesktop ? (
              <Table
                columns={transactionDetailsMarkingsTableColumns(
                  t,
                  fieldsForTransactionType,
                  transactionType,
                )}
                dataSource={mapTransactionDetailsMarkings(transactionDetailsMarkingsContent)}
                onSort={setSortMarkings}
                translationsKeys={ETransactionDetailsTranslationsKeys}
              />
            ) : (
              <CardList
                items={mapTransactionDetailsMarkings(transactionDetailsMarkingsContent)}
                translationsKeys={ETransactionDetailsTranslationsKeys}
                render={(data, index) => (
                  <TransactionDetailsMarkingsCard
                    data={data}
                    key={index}
                    transactionType={transactionType}
                    fieldsForTransactionType={fieldsForTransactionType}
                  />
                )}
              />
            )}
            {transactionDetailsMarkingsPaginator && (
              <Pagination
                count={transactionDetailsMarkingsPaginator.totalElements}
                onChangePage={(page) => setCurrentPageMarkings(page)}
                current={currentPageMarkings}
                perPage={perPageMarkings}
                onChangePerPage={(value) => setPerPageMarkings(value)}
                isLoading={isMarkingsPaginationLoading}
              />
            )}
          </Grid>
        )}
      </Accordion>
    );
  }
  if (
    transactionDetailsMarkingsContent &&
    transactionDetailsMarkingsContent.length === 1 &&
    transactionDetailsMarkingsContent[0].markings.length
  ) {
    return (
      <Accordion
        title={t(
          transactionType === ETransactionTypes.REPURPOSED
            ? 'transactionDetails.repurposedItemUpdates.accordion.name'
            : 'transactionDetails.markings.accordion.name',
        )}
        name="markingsDetails"
        expanded={isAllExpanded}
      >
        <StyledDetailsWrapper container style={{ rowGap: 1, marginBottom: '40px', padding: '4px !important' }} >
          <DetailsMarkingsItem
            data={transactionDetailsMarkingsContent[0]}
            transactionType={transactionType}
            fieldsForTransactionType={fieldsForTransactionType}
          />
        </StyledDetailsWrapper>
      </Accordion>
    );
  }
};
export { TransactionDetailsMarkingsSection };
